import { Button } from '@acpaas-ui/react-components';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { fetchReset } from '../actions/fetch.actions';
import { resourceReset } from '../actions/resources.actions';
import Tooltip from '../components/core/Tooltip';
import StakeholderAddSearchTab from '../components/dossier-shared/stakeholder-add/StakeholderAddSearchTab';
import StakeholderAddWorkedForSameSchoolTab from '../components/dossier-shared/stakeholder-add/StakeholderAddWorkedForSameSchoolTab';
import SelectField from '../components/forms/SelectField';
import {
  STAKEHOLDER_DOSSIER_PATHS,
  STAKEHOLDER_ROLES_CASETAFEL_OPTIONS,
  STAKEHOLDER_ROLES_CLARIFICATION_OPTIONS,
  STAKEHOLDER_ROLES_CONTACTMOMENTS_OPTIONS,
  STAKEHOLDER_ROLES_PROCEDURE_OPTIONS,
  STAKEHOLDER_ROLE_CONTACTMOMENT_OTHER
} from '../constants/dossier.constants';
import { MODALS } from '../constants/modals.constants';
import { useModal } from '../hooks/useModal.hook';
import { useSimpleSearch } from '../hooks/useSimpleSearch.hook';
import { CASETAFEL_STAKEHOLDERS_RESOURCE } from '../resources/casetafel.stakeholders.resource';
import { CLARIFICATION_STAKEHOLDERS_RESOURCE } from '../resources/clarification.stakeholders.resource';
import { CONTACTMOMENT_STAKEHOLDERS_RESOURCE } from '../resources/contactmoments.stakeholders.resource';
import { DOSSIER_CONTACTS_WORKED_FOR_SAME_SCHOOL_RESOURCE } from '../resources/dossier.contacts.worked.for.same.school.resource';
import { DOSSIER_STAKEHOLDER_CONTACTS_RESOURCE } from '../resources/dossier.stakeholder.contacts.resource';
import { PROCEDURE_STAKEHOLDERS_RESOURCE } from '../resources/procedure.stakeholders.resource';
import casetafelsService from '../services/api/casetafels.service';
import clarificationsService from '../services/api/clarifications.service';
import contactMomentsService from '../services/api/contactmoments.service';
import procedureService from '../services/api/procedure.service';
import AddContactModal from './admin/AddContactModal';
import Modal from './Modal';
import ModalTitle from './ModalTitle';

const TABS = { SEARCH: 0, WORKED_FOR_SAME_SCHOOL: 1 };

const StakeholderAddModal = ({ isOpen, dossierId, dossierPath, dossierSchools, itemId, onUpdated, onClose }) => {
  const dispatch = useDispatch();
  const tooltipId = useRef(uuid()).current;
  const modalContentRef = useRef(null);
  const addContactModal = useModal(MODALS.ADD_CONTACT_MODAL);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [role, setRole] = useState(
    [STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS, STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD].includes(dossierPath)
      ? STAKEHOLDER_ROLE_CONTACTMOMENT_OTHER
      : ''
  );
  const [selected, setSelected] = useState(null);
  const contactsResource = {
    ...DOSSIER_STAKEHOLDER_CONTACTS_RESOURCE,
    table: {
      ...DOSSIER_STAKEHOLDER_CONTACTS_RESOURCE.table,
      disablePagination: true,
      highlight: (item) => selected?.id === item?.id
    }
  };
  const search = useSimpleSearch(DOSSIER_STAKEHOLDER_CONTACTS_RESOURCE, 'name', true);
  const [activeTab, setActiveTab] = useState(TABS.SEARCH);
  const [showTable, setShowTable] = useState(false);

  const getRoleOptions = () => {
    switch (dossierPath) {
      case STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION:
        return STAKEHOLDER_ROLES_CLARIFICATION_OPTIONS;
      case STAKEHOLDER_DOSSIER_PATHS.CASETABLE:
      case STAKEHOLDER_DOSSIER_PATHS.COMMISSION:
        return STAKEHOLDER_ROLES_CASETAFEL_OPTIONS;
      case STAKEHOLDER_DOSSIER_PATHS.PROCEDURE:
        return STAKEHOLDER_ROLES_PROCEDURE_OPTIONS;
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS:
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD:
        return STAKEHOLDER_ROLES_CONTACTMOMENTS_OPTIONS;
      default:
        return [];
    }
  };

  const reset = () => {
    setRole('');
    setSelected(null);
    search.onChange('');
    setShowTable(false);
    dispatch(resourceReset(DOSSIER_STAKEHOLDER_CONTACTS_RESOURCE));
    dispatch(resourceReset(DOSSIER_CONTACTS_WORKED_FOR_SAME_SCHOOL_RESOURCE));
    dispatch(fetchReset(DOSSIER_CONTACTS_WORKED_FOR_SAME_SCHOOL_RESOURCE));
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleConfirm = (_e, inlineData) => {
    let submitFn = () => {};
    let stakeholdersResource;
    switch (dossierPath) {
      case STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION:
        submitFn = clarificationsService.addStakeholder;
        stakeholdersResource = CLARIFICATION_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.CASETABLE:
      case STAKEHOLDER_DOSSIER_PATHS.COMMISSION:
        submitFn = casetafelsService.addStakeholder;
        stakeholdersResource = CASETAFEL_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.PROCEDURE:
        submitFn = procedureService.addStakeholder;
        stakeholdersResource = PROCEDURE_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS:
        submitFn = contactMomentsService.addStakeholder;
        stakeholdersResource = CONTACTMOMENT_STAKEHOLDERS_RESOURCE;
        break;
      case STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD:
        return onUpdated(inlineData || { ...selected, role });
      default:
        break;
    }
    const options = {
      onSuccess: (resp) => {
        setIsSubmitting(false);
        handleClose();
        let newInvolved = [];
        if (dossierPath === STAKEHOLDER_DOSSIER_PATHS.PROCEDURE) {
          newInvolved = resp?.dossier?.[dossierPath]?.involved;
        } else {
          // when commission data is saved under caseTable
          newInvolved = resp?.dossier?.[
            dossierPath === STAKEHOLDER_DOSSIER_PATHS.COMMISSION ? STAKEHOLDER_DOSSIER_PATHS.CASETABLE : dossierPath
          ]?.find((item) => item.id === itemId)?.involved;
        }
        onUpdated(newInvolved, 'involved', itemId);
        dispatch(
          fetchReset({
            key:
              dossierPath === STAKEHOLDER_DOSSIER_PATHS.PROCEDURE
                ? stakeholdersResource.key
                : `${stakeholdersResource.key}_${itemId}`
          })
        );
      },
      onError: (message) => {
        setIsSubmitting(false);
        setSubmitError(message);
      }
    };
    setIsSubmitting(true);
    submitFn(dossierId, itemId, inlineData || { ...selected, role }, options);
  };

  const handleSelect = (item) => {
    if (selected && selected?.id === item?.id) {
      setSelected(null);
    } else {
      setSelected(item);
    }
  };

  const handleSearch = () => {
    setShowTable(true);
    setSelected(null);
  };

  const handleContactCreated = (createdContact) => {
    setActiveTab(TABS.SEARCH);
    const newSearch =
      `${createdContact?.spoContactData?.firstName ?? ''} ${createdContact?.spoContactData?.lastName ?? ''}`?.trim() ??
      '';
    search.onChange(newSearch);
    search.onSearch(null, newSearch);
    handleSearch();
    addContactModal.onClose();
  };

  const getChangeTabHandler = (tab) => () => {
    setSelected(null);
    setActiveTab(tab);
  };

  return (
    <>
      <Modal
        ref={modalContentRef}
        title={<ModalTitle title="Betrokkene toevoegen" />}
        isOpen={isOpen}
        isSubmitting={isSubmitting}
        onClose={handleClose}
        canConfirm={
          (!!role ||
            [
              STAKEHOLDER_DOSSIER_PATHS.PROCEDURE,
              STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS,
              STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD
            ].includes(dossierPath)) &&
          !!selected
        }
        onConfirm={handleConfirm}
        onConfirmLabel="Toevoegen"
        onConfirmIcon="ai-check-1"
        onConfirmType="success"
        width={1000}
        extraFooterActions={
          <Button iconLeft="ai-add" disabled={isSubmitting} onClick={addContactModal.onOpen} outline>
            Contact aanmaken
          </Button>
        }
      >
        <ul className="m-nav-tabs m-nav-tabs--left">
          <li>
            <Button
              className={activeTab === TABS.SEARCH ? 'is-active' : ''}
              negative
              onClick={getChangeTabHandler(TABS.SEARCH)}
            >
              Zoeken
            </Button>
          </li>
          <li>
            <Button
              className={activeTab === TABS.WORKED_FOR_SAME_SCHOOL ? 'is-active' : ''}
              negative
              onClick={getChangeTabHandler(TABS.WORKED_FOR_SAME_SCHOOL)}
            >
              Reeds mee samengewerkt
            </Button>
          </li>
        </ul>
        {activeTab === TABS.SEARCH ? (
          <StakeholderAddSearchTab
            search={search}
            contactsResource={contactsResource}
            onSearch={handleSearch}
            onSelect={handleSelect}
            showTable={showTable}
          />
        ) : (
          <StakeholderAddWorkedForSameSchoolTab
            selected={selected}
            onSelect={handleSelect}
            dossierId={dossierId}
            dossierSchools={dossierSchools}
          />
        )}
        <SelectField
          className="u-margin-bottom"
          label={
            dossierPath === STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION
              ? 'Rol voor vraagverheldering'
              : dossierPath === STAKEHOLDER_DOSSIER_PATHS.CASETABLE
              ? 'Rol voor casetafel'
              : dossierPath === STAKEHOLDER_DOSSIER_PATHS.COMMISSION
              ? 'Rol voor commissie'
              : [STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS, STAKEHOLDER_DOSSIER_PATHS.CONTACTMOMENTS_ADD].includes(
                  dossierPath
                )
              ? 'Rol voor contactmoment'
              : 'Rol voor opvolging'
          }
          options={getRoleOptions()}
          value={role}
          required={[
            STAKEHOLDER_DOSSIER_PATHS.CASETABLE,
            STAKEHOLDER_DOSSIER_PATHS.COMMISSION,
            STAKEHOLDER_DOSSIER_PATHS.CLARIFICATION
          ].includes(dossierPath)}
          onChange={setRole}
          disableSearch
          parentElement={modalContentRef.current}
          // flyoutRelative
        />
        {submitError && <small className="u-block u-margin-top-sm u-text-danger">{submitError}</small>}
      </Modal>
      <Tooltip id={tooltipId} />
      {addContactModal.isOpen && <AddContactModal {...addContactModal} onCreated={handleContactCreated} />}
    </>
  );
};

export default StakeholderAddModal;
